import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowDown, ArrowUp, Loader2, Plus, X } from "lucide-react";
import { PRIORITIES, type ProcessRecord, type RouteStep } from "@/types/production";

const field = "w-full h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring";
const today = () => new Date().toISOString().slice(0, 10);

export interface LinePreset {
  id: string;
  product_id: string;
  product_name: string;
  variant_name?: string | null;
  unit: string;
  /** Quantity not yet planned on a work order */
  remaining: number;
  delivery_date?: string | null;
  order_number: string;
}

/** Make a work order: for an order line (product and quantity come from it) or for stock. Its stages start as the product type's route. */
export function WorkOrderFormDialog({ open, line, onClose, onSaved }: { open: boolean; line?: LinePreset | null; onClose: () => void; onSaved: () => void }) {
  const { success, error } = useToast();
  const navigate = useNavigate();
  const [processes, setProcesses] = useState<ProcessRecord[]>([]);
  const [saving, setSaving] = useState(false);
  const [product, setProduct] = useState({ id: "", label: "", unit: "" });
  const [variants, setVariants] = useState<Array<{ id: string; name: string }>>([]);
  const [form, setForm] = useState({ variant_id: "", planned_qty: "", due_date: "", start_date: today(), priority: "NORMAL", notes: "" });
  const [codes, setCodes] = useState<string[]>([]);
  const [routeNote, setRouteNote] = useState("");
  const [addCode, setAddCode] = useState("");

  useEffect(() => {
    if (!open) return;
    setProduct(line ? { id: line.product_id, label: line.product_name, unit: line.unit } : { id: "", label: "", unit: "" });
    setVariants([]);
    setForm({ variant_id: "", planned_qty: line && line.remaining > 0 ? String(line.remaining) : "", due_date: line?.delivery_date?.slice(0, 10) || "", start_date: today(), priority: "NORMAL", notes: "" });
    setCodes([]);
    setRouteNote("");
    setAddCode("");
    api.get("/api/ProcessMaster/List").then((r) => setProcesses(unwrapArray<ProcessRecord>(r))).catch(() => setProcesses([]));
    if (line) loadRoute(line.product_id);
  }, [open, line?.id]);

  /** The product type's route becomes the starting stages */
  const loadRoute = async (productId: string) => {
    try {
      const p = unwrapObject<{ product_type?: string | null }>(await api.get(`/api/Product/ById/${productId}`));
      if (!p.product_type) {
        setCodes([]);
        setRouteNote("This product has no type, so pick its stages below.");
        return;
      }
      const route = unwrapArray<RouteStep>(await api.get(`/api/ProcessMaster/RouteFor/${p.product_type}`));
      setCodes(route.map((s) => s.process_code));
      setRouteNote(route.length ? "" : "No route is set up for this product type. Pick the stages below.");
    } catch {
      setCodes([]);
    }
  };

  const pickProduct = async (id: string, row: any) => {
    setProduct({ id, label: row ? `${row.name} (${row.code})` : "", unit: row?.unit || "" });
    setForm((f) => ({ ...f, variant_id: "" }));
    setVariants([]);
    if (!id) return;
    loadRoute(id);
    try {
      setVariants(unwrapArray<any>(await api.get(`/api/Product/Variants/${id}`)).map((v) => ({ id: v.id, name: v.name })));
    } catch {
      /* no shades to choose */
    }
  };

  const nameOf = (code: string) => processes.find((p) => p.code === code)?.name || code;
  const move = (i: number, d: number) =>
    setCodes((c) => {
      const n = [...c];
      const j = i + d;
      if (j < 0 || j >= n.length) return c;
      [n[i], n[j]] = [n[j]!, n[i]!];
      return n;
    });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!line && !product.id) return error("Validation Error", "Choose the product to make");
    if (!codes.length) return error("Validation Error", "Add at least one stage");
    if (!(Number(form.planned_qty) > 0)) return error("Validation Error", "Enter the quantity to make");
    setSaving(true);
    try {
      const res: any = await api.post("/api/WorkOrder/Insert", {
        order_line_id: line?.id,
        product_id: line ? undefined : product.id,
        variant_id: line ? undefined : form.variant_id || undefined,
        planned_qty: Number(form.planned_qty),
        due_date: form.due_date || undefined,
        start_date: form.start_date || undefined,
        priority: form.priority,
        process_codes: codes,
        notes: form.notes.trim() || undefined,
      });
      success("Work order made", "Its stages are planned from the start date");
      onSaved();
      onClose();
      if (res?.AddtionalData) navigate(`/production/${res.AddtionalData}`);
    } catch (err: any) {
      error("Could not make the work order", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  const unused = processes.filter((p) => !codes.includes(p.code));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-2xl md:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{line ? `Make goods for ${line.order_number}` : "New work order"}</DialogTitle>
          <DialogDescription>{line ? `${line.product_name}${line.variant_name ? ` · ${line.variant_name}` : ""}` : "Make a quantity of a product for stock, or choose it from an order line on the order page."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col min-h-0">
          <div className="space-y-4 py-2 text-sm overflow-y-auto max-h-[64vh] pr-1">
            {!line && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Product *</Label>
                  <SearchSelect endpoint="/api/Product/List" extraParams={{ active: "true" }} value={product.id} selectedLabel={product.label} getLabel={(row) => `${row.name} (${row.code})`} placeholder="Search products" onChange={pickProduct} />
                </div>
                <div className="space-y-1.5">
                  <Label>Shade</Label>
                  <select className={field} value={form.variant_id} onChange={(e) => setForm({ ...form, variant_id: e.target.value })} disabled={!variants.length}>
                    <option value="">{variants.length ? "-- No shade --" : "No shades"}</option>
                    {variants.map((v) => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label>Quantity * {product.unit && <span className="text-muted-foreground">({product.unit})</span>}</Label>
                <Input inputMode="decimal" value={form.planned_qty} onChange={(e) => setForm({ ...form, planned_qty: e.target.value })} className="h-10" />
              </div>
              <div className="space-y-1.5">
                <Label>Start on</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="h-10" />
              </div>
              <div className="space-y-1.5">
                <Label>Due by</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="h-10" />
              </div>
              <div className="space-y-1.5">
                <Label>Priority</Label>
                <select className={field} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                  {Object.entries(PRIORITIES).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Stages</Label>
              {routeNote && <p className="text-xs text-amber-600">{routeNote}</p>}
              <div className="rounded-lg border border-border divide-y divide-border">
                {codes.map((c, i) => (
                  <div key={c} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                    <span className="w-5 text-muted-foreground">{i + 1}</span>
                    <span className="flex-1 font-medium">{nameOf(c)}</span>
                    <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => move(i, -1)} title="Move up"><ArrowUp className="size-3.5" /></button>
                    <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => move(i, 1)} title="Move down"><ArrowDown className="size-3.5" /></button>
                    <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => setCodes(codes.filter((x) => x !== c))} title="Remove"><X className="size-3.5" /></button>
                  </div>
                ))}
                {codes.length === 0 && <p className="px-3 py-3 text-xs text-muted-foreground">No stages yet.</p>}
              </div>
              <div className="flex gap-2">
                <select className={`${field} h-9`} value={addCode} onChange={(e) => setAddCode(e.target.value)}>
                  <option value="">Add a stage…</option>
                  {unused.map((p) => (
                    <option key={p.code} value={p.code}>{p.name}</option>
                  ))}
                </select>
                <Button type="button" size="sm" variant="outline" className="h-9 gap-1" disabled={!addCode} onClick={() => { setCodes([...codes, addCode]); setAddCode(""); }}>
                  <Plus className="size-3.5" /> Add
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving} className="gap-1.5">
              {saving && <Loader2 className="size-4 animate-spin" />}
              Make work order
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
